/**
 * Default meta description for the home page and SEO fallbacks.
 * Aim for 130–160 characters. Keep `public/index.html` meta/og/twitter in sync.
 */
export const DEFAULT_SITE_META_DESCRIPTION =
  'Challenge friends on EWIO: peer-to-peer skill crypto bets with smart-contract escrow and on-chain settlement—no middleman. CS2, Dota 2, Rocket League, LoL, and more.';

/** Preferred public origin (www). CRA inlines REACT_APP_* at build time. */
export function getSiteUrl() {
  const raw = (process.env.REACT_APP_SITE_URL || 'https://www.ewio.org').trim();
  return raw.replace(/\/+$/, '');
}

/**
 * Stable absolute URL for the current path (no query or hash).
 * @param {string} pathname - e.g. `/blog/foo` or `/`
 */
export function canonicalFromPath(pathname) {
  const base = getSiteUrl();
  let path = pathname || '/';
  if (!path.startsWith('/')) path = `/${path}`;
  path = path.replace(/\/+/g, '/');
  if (path.length > 1 && path.endsWith('/')) path = path.slice(0, -1);
  if (path === '/') return `${base}/`;
  return `${base}${path}`;
}

/**
 * Normalize `url` prop or current location into a single canonical URL on {@link getSiteUrl}.
 * @param {string} [input] - Full URL, path, or omit to use `window.location.pathname`
 * @param {string} [pathnameFallback] - Used when `window` is unavailable (e.g. tests)
 */
export function resolveCanonicalUrl(input, pathnameFallback = '/') {
  if (input == null || input === '') {
    const p =
      typeof window !== 'undefined' && window.location
        ? window.location.pathname
        : pathnameFallback;
    return canonicalFromPath(p);
  }
  const s = String(input).trim();
  if (s.startsWith('http://') || s.startsWith('https://')) {
    try {
      const u = new URL(s);
      return canonicalFromPath(u.pathname);
    } catch {
      return canonicalFromPath(pathnameFallback);
    }
  }
  return canonicalFromPath(s.startsWith('/') ? s : `/${s}`);
}

/** JSON-LD Organization; reference from Article/BlogPosting via publisher @id (site + "/#organization"). */
export function organizationJsonLd() {
  const site = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${site}/#organization`,
    name: 'EWIO',
    alternateName: 'IO Gaming Platform',
    url: site,
    logo: `${site}/logo512.png`,
    sameAs: ['https://twitter.com/IOGaming'],
  };
}

/** JSON-LD WebSite for the home page (no SearchAction — no dedicated site search URL). */
export function websiteJsonLd() {
  const site = getSiteUrl();
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${site}/#website`,
    name: 'EWIO',
    alternateName: 'IO Gaming Platform',
    url: site,
    description: DEFAULT_SITE_META_DESCRIPTION,
    publisher: { '@id': `${site}/#organization` },
  };
}

/**
 * BreadcrumbList JSON-LD.
 * @param {{ name: string, path: string }[]} items - `path` is pathname only, e.g. `/blog`
 */
export function breadcrumbListJsonLd(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      item: canonicalFromPath(it.path),
    })),
  };
}
